import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, selectLocation } from 'src/stores';
import { Button, FormLabel } from 'src/components/forms';
import { Icon } from 'src/components/Icon';
import { gaTracker } from 'src/utils/hooks/useTracker';
import { useLazyFindByLocationQuery } from 'src/services/dealerAPI';
import {
  setActivatorEventTriggered,
  trackSearchCity,
} from 'src/utils/tracking/trackingSlice';
import { SelectedLocation } from 'src/applications/DealerLocator/components/DealerInterface';
import {
  setCurrentAgencyId,
  setDealerSearchModalStep,
  setUserLocation,
  setOpenModalFromDealerActivator,
  setOpenModalFromPromotion,
} from 'src/applications/DealerLocator/utils/dealerSlice';
import { DealerLocationSearch } from 'src/applications/DealerLocator/components/DealerLocationSearch';
import { openEncDeLo } from 'src/applications/EncapsulatedDealerSearch';
import {
  useLazyGraphqlFindByLocationQuery,
  useLazyGraphqlFindDealerQuery,
} from 'src/services/graphql/graphqlDealersAPI';
import { Loader } from 'src/components/Loader';
import classNames from 'classnames';
import { stringToBoolean } from 'src/utils/helpers/featureHelpers';
import { getStyling } from './DealerSearchActivator.styles';
import { mapProducts } from 'src/utils/tracking/trackingHelper';
import {
  PAGETYPEGA4,
  SUPPORTED_TBU,
  isNonEcommerce,
} from 'src/utils/variables';
import { useObserveLegacyCart } from 'src/utils/hooks/useObserveLegacyStore';
import { useTrackDealerLocation } from 'src/applications/DealerLocator/utils/useTrackDealerLocation';
import {
  dealerSearchAddrImpression,
  fetchLocation,
  getGeocodeByPlaceId,
  prepareFilters,
} from 'src/applications/DealerLocator/utils/dealerSearchHelper';

interface DealerSearchActivatorProps {
  variant?: 'default' | 'pdp' | 'promotion';
  promotionId?: string;
  isGraphql?: string;
  name?: string;
  error?: string;
}
const DealerSearchActivator = ({
  variant = 'default',
  promotionId = '',
  isGraphql = 'false',
  name,
  error,
}: DealerSearchActivatorProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const styles = getStyling();
  const cart = useObserveLegacyCart();

  const promotionAgency = useAppSelector(
    (state) => state.dealerSearch?.dealers.promotionAgency
  );
  const showManualDealerForm = useAppSelector(
    (state) => state.formRegistration.showForm
  );
  const activatorEventTriggered = useAppSelector(
    (state) => state.tracking.activatorEventTriggered
  );

  const isPromotion = variant === 'promotion';

  const [result, setResult] = useState<any>({
    isLoading: true,
    isSuccess: false,
    isFetching: false,
  });
  const [triggerGaaS, resultGaaS] = useLazyFindByLocationQuery();
  const [triggerGraphql, resultGraphql] = useLazyGraphqlFindByLocationQuery();
  const [dealerByPromotionId, dealersResult] = useLazyGraphqlFindDealerQuery();
  const { TrackDealerLocation } = useTrackDealerLocation();
  const isGraphqlBoolean = stringToBoolean(isGraphql);
  const productDetailsSelectTracking = gaTracker({
    event: 'dle_product_details_select',
  });

  const promotionInteractionTracker = gaTracker({
    event: 'dle_promotion_interaction',
  });

  const isEventTrigger = useRef(false);

  const triggerProductDetailsSelect = (item: string, text: string) => {
    productDetailsSelectTracking({
      product_details_clicked_element: item,
      element_text: text,
    });
  };

  // window object
  const tbuFilter = SUPPORTED_TBU;

  // useState
  const [location, setLocation] = useState<SelectedLocation | null>(null);
  const [requestId, setRequestId] = useState<string | number | undefined>(
    result?.requestId
  );

  useEffect(() => {
    if (isGraphqlBoolean) {
      setResult(resultGraphql);
    } else {
      setResult(resultGaaS);
    }
  }, [resultGaaS, resultGraphql]);

  // useEffect
  useEffect(() => {
    if (promotionId) {
      dealerByPromotionId({
        filter: {
          promotions: promotionId,
        },
      });
    }
    if (!activatorEventTriggered) {
      const products =
        cart && cart?.isloaded && cart?.tires.length && !isNonEcommerce
          ? mapProducts(cart.tires)
          : undefined;

      if ((cart && cart.isloaded) || isNonEcommerce) {
        dealerSearchAddrImpression({
          is_popup: 0,
          dealer_search_type: PAGETYPEGA4 || 'homepage',
          products,
        });
        dispatch(setActivatorEventTriggered(true));
      }
    }
  }, []);

  useEffect(() => {
    dispatch(trackSearchCity({ labelType: location?.label }));
    if (location?.value?.place_id) {
      findDealersByLocation(location, isGraphqlBoolean);
    }
  }, [location]);

  useEffect(() => {
    if (result?.isFetching) {
      if (requestId !== result.startedTimeStamp)
        setRequestId(result.startedTimeStamp);
    }
    if (result.isSuccess) {
      if (result.fulfilledTimeStamp && result.startedTimeStamp) {
        if (result.fulfilledTimeStamp > result.startedTimeStamp) {
          if (result.data?.dealers?.length > 0) {
            dispatch(
              setCurrentAgencyId(result.data.dealers[0].dealer.agencyId)
            );
          }
          if (location) {
            selectLocation(
              location?.value?.place_id,
              location.label,
              result.data?.dealers
            );
          }
        }
      }
    }
    if (!result.isFetching && result.isSuccess && location) {
      !isEventTrigger.current &&
        PAGETYPEGA4 === 'promotion landing page' &&
        promotionInteractionTracker({
          element_text: undefined,
          element_url: undefined,
          promotion_interaction_type: 'dealer widget',
          element_name: 'Locator - find a location',
          element_value: location.label,
        });
      isEventTrigger.current = false;
      openEncDeLo();
      dispatch(setDealerSearchModalStep(1));
    }
  }, [requestId, result]);

  // functions
  const findDealersByLocation = async (
    location: SelectedLocation,
    isGraphQl: boolean
  ) => {
    try {
      const { coordinates } = await getGeocodeByPlaceId(
        location.value?.place_id
      );
      const { lat, lng } = coordinates;
      const queryFilters = prepareFilters({ tbuFilter });
      if (isGraphQl) {
        dispatch(setOpenModalFromDealerActivator(true));
        if (isPromotion) dispatch(setOpenModalFromPromotion(true));
        triggerGraphql({
          lat: lat(),
          lon: lng(),
          filter: {
            features: queryFilters.length > 0 ? queryFilters : undefined,
          },
        });
      } else {
        triggerGaaS({
          lat: lat(),
          lon: lng(),
          features: queryFilters.length > 0 ? queryFilters : undefined,
        });
      }

      dispatch(
        setUserLocation({
          placeId: location.value?.place_id,
          lat: lat(),
          lng: lng(),
          label: location.label,
        })
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const fetchUserLocation = async (
    latitude = null,
    longitude = null,
    selectedText = ''
  ) => {
    try {
      const { lat, lng, placeId, label, value } = await fetchLocation(
        latitude,
        longitude,
        selectedText
      );
      setLocation({
        label,
        value,
      });
      PAGETYPEGA4 === 'promotion landing page' &&
        promotionInteractionTracker({
          element_text: t('dealersearch.label.use-current-location'),
          element_url: undefined,
          promotion_interaction_type: 'dealer widget',
          element_name: 'Locator - use current location',
          element_value: label,
        });
      isEventTrigger.current = true;
      TrackDealerLocation({
        label,
        value,
      });
      dispatch(
        setUserLocation({
          placeId,
          lat: lat(),
          lng: lng(),
        })
      );
    } catch (error) {
      console.error(
        "Failed to retrieve your location. Please visit your browser's settings and allow geolocation permission"
      );
      throw error;
    }
    isNonEcommerce &&
      triggerProductDetailsSelect('find dealer', 'use current location');
  };

  const availableStores = () =>
    dealersResult?.isSuccess ? (
      <div
        className="text-base text-brand-primary md:underline pb-2 cursor-pointer"
        onClick={() => {
          dispatch(setOpenModalFromDealerActivator(true));
          openEncDeLo();
          dispatch(setDealerSearchModalStep(1));
          PAGETYPEGA4 === 'promotion landing page' &&
            promotionInteractionTracker({
              element_text: undefined,
              element_url: undefined,
              promotion_interaction_type: 'dealer link',
              element_name: "dealer's availability link",
              element_value: '',
            });
        }}
      >
        {t('dealersearch.activator.label.available-stores').replace(
          '{0}',
          dealersResult.data?.pagination?.totalResult || 0
        )}
      </div>
    ) : (
      <Loader />
    );

  return (
    <>
      {(variant === 'default' || isPromotion) && (
        <section data-testid="dealer-search-no-results">
          <div className="min-h-max text-body">
            <div
              className={classNames(
                'md:max-w-[768px] lg:max-w-[992px] xl:max-w-[1200px] xl:mx-auto',
                {
                  'py-4': !promotionId,
                }
              )}
            >
              {promotionId && !isPromotion && availableStores()}
              <div
                className={`text-left pt-2 font-bold mb-1 ${styles['title-color']}`}
              >
                <FormLabel required={isPromotion} error={error}>
                  {isPromotion
                    ? t('promotion.dealer-section.dealer.by.location.label')
                    : t('service-filter.filter.location.title')}
                </FormLabel>
              </div>
              <div className="flex flex-row">
                <div
                  className="w-full sm:!w-[28rem]"
                  data-testid="dealer-location-search"
                >
                  <DealerLocationSearch
                    location={
                      isPromotion &&
                      (promotionAgency.currentAgencyId || showManualDealerForm)
                        ? location
                        : null
                    }
                    setLocation={setLocation}
                    fetchUserLocation={fetchUserLocation}
                    isDealerSearchActivator={isPromotion}
                    name={name}
                    error={error}
                    promotionIdPresent={isPromotion}
                  />
                </div>
              </div>
              {!isPromotion && (
                <div className="flex flex-row mt-4">
                  <div className="basis-auto text-left">
                    <Icon
                      iconName="MyLocation"
                      className={`inline ${styles['myLocation-icon-color']}`}
                    />
                  </div>
                  <div>
                    <Button
                      className={`align-middle hover:underline pl-3 ${styles['myLocation-button-color']}`}
                      onClick={() => fetchUserLocation()}
                      variant="link"
                    >
                      {t('dealersearch.label.use-current-location')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      {variant === 'pdp' && (
        <>
          <div className="hidden">
            <DealerLocationSearch
              location={null}
              setLocation={setLocation}
              fetchUserLocation={fetchUserLocation}
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => fetchUserLocation()}
            iconRight={{ iconName: 'MyLocation' }}
          >
            {t('dealersearch.label.use-current-location')}
          </Button>
        </>
      )}
    </>
  );
};

export default DealerSearchActivator;
